import { useNavigate } from 'react-router'

import type { AlertResponse } from '@/api/types/Alert'
import Avatar from '@/elements/Avatar'
import ExternalLink from '@/elements/ExternalLink'
import Pill from '@/elements/Pill'
import Tag from '@/elements/Tag'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Timestamp from '@/elements/Timestamp'
import { formatAsTitle } from '@/utils/format'
import { useGetUser } from '@/api/user'
import { useGetPrivateSubject } from '@/api/profile'
import useBreakpoints from '@/hooks/useBreakpoints'

import './WatchdogAlertCard.scss'

interface WatchdogAlertCardProps {
  alert: AlertResponse
  showIndividual?: boolean
}

function resolveUserId(value?: string | { id: string } | null) {
  if (!value) return undefined
  return typeof value === 'string' ? value : value.id
}

function WatchdogAlertCard({
  alert,
  showIndividual = false,
}: WatchdogAlertCardProps) {
  const navigate = useNavigate()
  const { isMobile } = useBreakpoints()

  const { id, status, alertDetails } = alert
  const { source, title, createdAt, sourceHref } = alertDetails

  const assignedToId = resolveUserId(alert.assignedTo)
  const escalatedToId = resolveUserId(alert.escalateTo)

  const { data: assignedToData } = useGetUser(assignedToId)
  const { data: escalatedToData } = useGetUser(escalatedToId)

  const assignedToUser = assignedToData?.data?.user
  const escalatedToUser = escalatedToData?.data?.user
  const numOfAssignees = [assignedToUser, escalatedToUser].filter(Boolean).length

  const { data: individualData, isLoading: individualLoading } =
    useGetPrivateSubject(alert.subject?.id, showIndividual)

  const individual = individualData?.data

  return (
    <Flex
      wfull
      hfull
      inline
      justify='space-between'
      align={!isMobile ? 'center' : 'flex-end'}
      gap={3}
      pt={3}
      pb={4}
      px={isMobile ? 3 : 4}
      className='alert-card'
      onClick={() => navigate(`/alert/${id}`)}
      style={{ position: 'relative' }}
    >
      <Flex wfull gap={2} grow>
        <Flex inline align='center' gap={2}>
          <Pill variant='status' status={status}>
            {formatAsTitle(status)}
          </Pill>
          <Tag variant={source}>{source}</Tag>
        </Flex>
        <Flex gap={1}>
          {showIndividual && !individualLoading && (
            <Flex inline align='center' gap={2}>
              <Avatar
                size='extraSmall'
                name={individual?.user.details.fullName || ''}
                src={individual?.user.assets?.PROFILE_PICTURE || ''}
              />
              <Text color='black-light-40'>
                {individual?.user.details.fullName || '--'}
                {' • '}
                {individual?.group?.name || '--'}
              </Text>
            </Flex>
          )}
          <Text variant='h4'>{title}</Text>
        </Flex>
        <Flex
          inline
          wfull
          align='center'
          gap={3}
          pr={isMobile ? 2 : 'unset'}
          gv='unset'
          wrap
          style={{
            ...isMobile ? {
              width: `calc(100% - ${numOfAssignees * 40}px)`,
              minHeight: 48
            } : null
          }}
        >
          <Timestamp from={createdAt || ''} />
          <ExternalLink
            to={sourceHref || ''}
            variant='light'
            onClick={e => e.stopPropagation()}
          >
            <Text variant='footnote'>Source</Text>
          </ExternalLink>
        </Flex>
      </Flex>
      <Flex
        inline
        stack
        mb={isMobile ? 4 : 'none'}
        mr={isMobile ? 3 : 'none'}
        style={{
          ...isMobile ? {
            position: 'absolute',
            right: 0,
            bottom: 0,
          } : null
        }}
      >
        <Avatar
          size='small'
          name={assignedToUser?.details.fullName}
          src={assignedToUser?.assets?.PROFILE_PICTURE || ''}
        />
        {escalatedToUser && (
          <Avatar
            size='small'
            name={escalatedToUser.details.fullName}
            src={escalatedToUser.assets?.PROFILE_PICTURE || ''}
          />
        )}
      </Flex>
    </Flex>
  )
}

export default WatchdogAlertCard
